import { useEffect, useState, useMemo } from "react";
import { Evaluaciones, Subdimensiones } from "../../../../../interfaces/Evaluations";
import Timeline from "../Timeline/Timeline";
import FixedTable from "../Tables/FixedTable";
import InstitucionalDesing from "../../InstitucionalDesing";
import { useAppSelector } from "../../../../../redux/hooks";
import MobileFixedTableStructural from "../Tables/MobileFixedTableStructural";

interface EvaluationSubdimensionStructuralProps {
  selected_type_indicator: { name: string, id: number };
  evaluation_subDimension: Subdimensiones[];
}

const EvaluationSubdimensionStructural = ({ selected_type_indicator, evaluation_subDimension }: EvaluationSubdimensionStructuralProps) => {
  const { selectedRight } = useAppSelector((state) => state.indicator_display_filter);
  const { selected_dimension } = useAppSelector((state) => state.evaluator);

  const [period, setPeriod] = useState("");
  const [right, setRight] = useState('');
  const [rightId, setRightId] = useState('');

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);

      setPeriod(params.get("periodo") || "");
      setRight(params.get('right') || '');
      setRightId(params.get('rightId') || '');
    }
  }, []);

  const extractLawNumber = (norma?: string | null, nombre?: string | null): number => {
    const s = `${norma ?? ""} ${nombre ?? ""}`;
    const matches = s.match(/\d+(?:[.,]\d+)*/g);
    if (!matches) return Number.NEGATIVE_INFINITY; // sin número → al final
    return Math.max(...matches.map((m) => parseInt(m.replace(/[.,]/g, ""), 10)));
  };

  const combinedEvaluations = useMemo(() => {
    return evaluation_subDimension
      .map((eva) => eva.evaluaciones)
      .filter((ev): ev is Evaluaciones => !!ev)
      .reduce((acc, current) => {
        Object.keys(current).forEach((year) => {
          acc[year] = acc[year] ? [...acc[year], ...current[year]] : current[year];
        });
        return acc;
      }, {} as Evaluaciones);
  }, [evaluation_subDimension]);

  const orderedEvaluations: Evaluaciones = useMemo(() => {
    const byYear: Evaluaciones = {};
    Object.keys(combinedEvaluations)
      .sort((a, b) => parseInt(b) - parseInt(a))
      .forEach((year) => {
        const seen = new Map<number, (typeof combinedEvaluations)[string][number]>();
        for (const ev of combinedEvaluations[year]) {
          if (!seen.has(ev.indicador_estructural_id)) seen.set(ev.indicador_estructural_id, ev);
        }
        const arr = Array.from(seen.values());
        arr.sort(
          (a, b) =>
            extractLawNumber(b.norma, b.nombre) -
            extractLawNumber(a.norma, a.nombre)
        );
        byYear[year] = arr;
      });
    return byYear;
  }, [combinedEvaluations]);

  const evaluationSubdimensionSorted: Subdimensiones[] = useMemo(() => {
    if (evaluation_subDimension.length === 0) return evaluation_subDimension;

    const sorted = evaluation_subDimension.map((sub, idx) => {
      if (!sub.evaluaciones) return sub;

      const newEval: Evaluaciones = {};
      const years = Object.keys({ ...sub.evaluaciones, ...orderedEvaluations });

      years.forEach((year) => {
        const order = orderedEvaluations[year] ?? [];
        const current = sub.evaluaciones![year] ?? [];

        const byId = new Map(current.map((e) => [e.indicador_estructural_id, e]));

        if (order.length > 0) {
          const reordered: typeof current = [];
          for (const ev of order) {
            const hit = byId.get(ev.indicador_estructural_id);
            if (hit) reordered.push(hit);
          }

          if (idx === 0) {
            const completed = order.map((ev) => byId.get(ev.indicador_estructural_id) ?? ev);
            newEval[year] = completed;
          } else {
            newEval[year] = reordered;
          }
        } else {
          newEval[year] = [...current].sort(
            (a, b) =>
              extractLawNumber(b.norma, b.nombre) -
              extractLawNumber(a.norma, a.nombre)
          );
        }
      });

      return { ...sub, evaluaciones: newEval };
    });

    return sorted;
  }, [evaluation_subDimension, orderedEvaluations]);

  return (
    <>
      <h3 className="text-tmedium md:text-tlarge mt-8 mb-6 text-primary-10">
        Marco Normativo. Diagnóstico de compromisos estatales      </h3>

      {/* Mobile */}
      <div className="block md:hidden">
        <MobileFixedTableStructural
          evaluation_subDimension={evaluationSubdimensionSorted}
          selected_type_indicator={selected_type_indicator}
          period={period}
          selected_dimension={selected_dimension}
          right={{ right, rightId }}
        />
      </div>

      {/* Desktop */}
      <div className="hidden md:block">
        <FixedTable
          evaluation_subDimension={evaluationSubdimensionSorted}
          selected_type_indicator={selected_type_indicator}
          period={period}
          selected_dimension={selected_dimension}
          right={{ right, rightId }}
        />
      </div>

      <div className="pb-4 md:pb-0 mt-10 md:mt-0 border-y border-t-[#DCE4E9] border-b-[#DCE4E9] md:border-none">
        <div className="text-tmedium md:text-tlarge mt-4 md:mt-10 mb-6">
          Leyes a lo largo del tiempo
        </div>
        <Timeline
          laws={orderedEvaluations}
          period={period}
        />
      </div>

      <div className="mt-10"></div>
      <InstitucionalDesing institucional={selectedRight?.[0]?.texto_modal ?? ""} />
      <div className="mt-10"></div>
    </>
  );
};

export default EvaluationSubdimensionStructural;